/* (c) Dalineage, s.r.o. 2020-2025, all rights reserved */
package com.dalineage.client.diagram

import org.scalajs.dom

import typings.gojs.{mod => go}

import com.dalineage.common
import common.diagram.adt.DiagramADT.DiagramData
import common.diagram.DiagramDataSerializer.GoJSSerializer
import common.adt.BatchInfoADT.BatchInfo

import com.dalineage.client

import org.scalablytyped.runtime.StringDictionary

import scala.util.chaining._
import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import io.circe.{ Json, DecodingFailure}
import cats.syntax.all._
import client.LineageTS._
import io.circe
import go.Part

object GoJSDiagram {

  var batchInfo: Option[BatchInfo] = None

  trait DiagramUserAction

  case class DiagramNodeSelected(selected: Option[StringDictionary[?]]) extends DiagramUserAction
  case class DiagramLinkSelected(selected: Option[go.Link]) extends DiagramUserAction
  case class ColumnSelected(selected: Option[(StringDictionary[?],go.Panel)]) extends DiagramUserAction
  case class ModelChanged(modelJson: String) extends DiagramUserAction
  case class KeyboardEvent(event: dom.KeyboardEvent) extends DiagramUserAction
  case class LoadJson(url: String, lFn: circe.Json => Unit) extends DiagramUserAction

  var diagram: go.Diagram = null

  var diagramDiv: dom.html.Div = null

  var selectedColumn: Option[(StringDictionary[?],go.Panel)] = None

  def gridLayout(): Unit = setLayout(new go.GridLayout)
  def layeredDigraphLayout(): Unit = setLayout(new go.LayeredDigraphLayout)
  def circularLayout(): Unit = setLayout(new go.CircularLayout)

  def setLayout(layout: go.Layout): Unit = {
    val selection = diagram.selection
    selection.size match {
      case 0 => diagram.layout = layout
      case _ =>
        selection.map{ _ match {
          case group:go.Group => group.layout = layout
          case ee @ _ => println(s"W: no group selected!")
        }}
    }
  }

  var userActionFn: DiagramUserAction => Unit = null

  def open(
      contentDiv: dom.html.Div,
      user: String,
      id: String,
      sourceId: Option[String],
      optKey: Option[String],
      userActionFn: DiagramUserAction => Unit): Unit = {

    val lFn: circe.Json => Unit = { json =>
      contentDiv.innerHTML = s"""<div class="help">Lineage diagram data loaded</div>"""
      json.as[DiagramData] match {
        case Left(err) => client.Console.msgBox(s"Error decoding diagram data JSON: " + "\n" + err)
        case Right(dd) =>

          client.Console.msgBox(s"Lineage loaded, user $user id $id sourceId $sourceId")

          Future.successful(dd.toGoJS())
            .map(_.tap(_ => init(contentDiv, userActionFn)))
            .map(_.noSpaces)
            .map(go.Model.fromJson(_).asInstanceOf[go.GraphLinksModel])
            .recoverWith{
              case failure: DecodingFailure => Future.failed(new Exception(s"Failed to parse GoJS JSON: ${failure.getMessage}", failure))
              case e => Future.failed(e)
            }
            .flatMap((model: go.GraphLinksModel) =>
              client.IDBDatabaseService(verbose = false)
                .addLineage()(model, true)
            )
            .flatMap(_ => open())
      }
    }

    val url = sourceId match {
      case Some(sid) =>
        optKey match {
          case Some(key) =>
            s"web/batch/$user/$id/$sid/$key"
          case None =>
            s"web/batch/$user/$id/$sid"
        }
      case None => s"web/batch/$user/$id"
    }

    contentDiv.innerHTML = s"""<div class="help">Loading diagram data</div>"""
    userActionFn( this.LoadJson(url,lFn) )
  }

  def open(): Future[Unit] =
    client.IDBDatabaseService(verbose = false).getLineage()
    .map(showModel(_))

  def open(diagramData: DiagramData): Unit = {
    // val collapsedTables: Set[String] = diagramData.collapsedTables
    val modelJson: String = diagramData.toGoJS().noSpaces
    val model: go.GraphLinksModel = go.Model.fromJson(modelJson).asInstanceOf[go.GraphLinksModel]
    showModel(model)
  }

  def init(div: dom.html.Div, userActionFn: DiagramUserAction => Unit): Unit = {
    diagramDiv = div

    this.userActionFn = userActionFn
    assert(diagramDiv != null, "Diagram div not initialized!")
    if( diagram == null ) {
      diagram = new go.Diagram(diagramDiv)
    }
    diagramDiv.children(0).id = "diagramWindow"
  }

  def highlightColumn(col: go.Panel, hi: Boolean)(implicit diagram: go.Diagram): Unit = {
    col.background = if( hi ) "dodgerblue" else "white"
  }

  def selectNode(key: String): Unit =
    diagram.findNodeForKey(key) match {
      case null => dom.console.warn(s"Node not found '$key'!")
      case node: go.Node => selectNode(node)
    }

  def selectNode(node: go.Node): Unit =
    diagram.select(node)
    diagram.scrollToRect(node.actualBounds)

  def showModel(model: go.Model): Unit = {

    val diagramUserActionFn: DiagramUserAction => Unit = { action =>
      action match {
        case DiagramNodeSelected(optData) =>
          optData.map{ data =>
            selectedColumn.map{ case (_, col) => highlightColumn(col, false)(using diagram) }
            selectedColumn = None

            val key = data.get("key").toString
            if( data.get("linefrom").isDefined ) {
              val sourceId = data.get("sourceId").get.toString
              val linefrom: Int = data.get("linefrom").get.toString.toInt
              val columnfrom: Int = data.get("columnfrom").get.toString.toInt
              val lineto: Int = data.get("lineto").get.toString.toInt
              val columnto: Int = data.get("columnto").get.toString.toInt
              client.Editor.selectCode(sourceId, linefrom, columnfrom, lineto, columnto)
            }
            val properties = DiagramContextMenu.menu(data)
            client.PropertyPanel.setProperties(properties)
          }
          userActionFn(action)

        case ColumnSelected(optData) =>
          selectedColumn.map{ case (_, col) => highlightColumn(col, false)(using diagram) }
          selectedColumn = optData
          optData.map{ case (_, col) => highlightColumn(col, true)(using diagram) }
          optData.map{ _ => diagram.clearSelection() }

          userActionFn(action)

        case KeyboardEvent(event) if event.keyCode == 13 =>
          diagram.selection.each (
            _.pipe{
              case group: go.Group if group.isSubGraphExpanded =>
                group.memberParts
                  .filter(_.isInstanceOf[go.Node])
                  .first()
                  .pipe{
                    case node: go.Node => node.tap(diagram.select)
                    case _ => group
                  }
                  .pipe(_.data.asInstanceOf[LineageNode])
              case node: go.Part =>
                node.data.asInstanceOf[LineageNode]
                .tap(node =>
                  model.setDataProperty(node, "expanded", !node.expanded)
                )
            }
            .pipe(DiagramContextMenu.menu)
            .pipe(client.PropertyPanel.setProperties)
          )
        case KeyboardEvent(event) if event.keyCode == 32 =>
          diagram.selection.each(
            _.data.asInstanceOf[LineageNode]
              .tap(node => model.setDataProperty(node, "expanded", !node.expanded))
              .pipe(DiagramContextMenu.menu)
              .pipe(client.PropertyPanel.setProperties)
          )

        case KeyboardEvent(event) if event.keyCode == 27 =>
          val selectedPart = diagram.selection.first()
          if (selectedPart != null)
            val data = selectedPart.data.asInstanceOf[StringDictionary[?]]
            data.get("group") match
              case Some(parent) =>
                val parentPart = diagram.findNodeForKey(parent.asInstanceOf[String])
                diagram.select(parentPart)
                val data = parentPart.data.asInstanceOf[StringDictionary[?]]
                val properties = DiagramContextMenu.menu(data)
                client.PropertyPanel.setProperties(properties)
              case None =>


        case KeyboardEvent(event)  =>
          val selectedPart = diagram.selection.first()
          val e = diagram.lastInput.event.asInstanceOf[dom.KeyboardEvent]
          if (selectedPart != null) {
            val newPart: Option[go.Part] = e.key match {
              case "ArrowUp"    =>
                Some(findNearestNode(selectedPart, "up"))
              case "ArrowDown"  =>
                Some(findNearestNode(selectedPart, "down"))
              case "ArrowLeft"  =>
                Some(findNearestNode(selectedPart, "left"))
              case "ArrowRight" =>
                Some(findNearestNode(selectedPart, "right"))
              case _ =>
                None
            }
            newPart match
              case Some(part) =>
                diagram.select(part)
                diagram.scrollToRect(part.actualBounds)
                e.preventDefault()
              case None =>
                userActionFn(this.KeyboardEvent(e))
          } else {
            //tbd filter out keys, enter, esc etc?
            userActionFn(this.KeyboardEvent(e))
          }

        case _ => userActionFn(action)
      }
    }

    diagram.maxSelectionCount = 1

    diagram.nodeTemplate = LineageTemplate(diagramUserActionFn)(using diagram)

    diagram.groupTemplate = GroupTemplate(diagramUserActionFn)

    diagram.linkTemplate = LinkTemplate()

    diagram.model = model
  }

  // Helper function to find the nearest node in a given direction
  def findNearestNode(currentPart: go.Part, direction: String): go.Part = {
    val diagram = currentPart.diagram
    val currentBounds = currentPart.actualBounds
    var nearestPart: go.Part = null
    var nearestDistance = Double.MaxValue

    diagram.nodes.each { part =>
      if (part != currentPart) {
        val partBounds = part.actualBounds
        var distance = Double.MaxValue
        direction match {
          case "up" =>
            if (partBounds.bottom < currentBounds.top) {
              distance = currentBounds.top - partBounds.bottom
            }
          case "down" =>
            if (partBounds.top > currentBounds.bottom) {
              distance = partBounds.top - currentBounds.bottom
            }
          case "left" =>
            if (partBounds.right < currentBounds.left) {
              distance = currentBounds.left - partBounds.right
            }
          case "right" =>
            if (partBounds.left > currentBounds.right) {
              distance = partBounds.left - currentBounds.right
            }
        }
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestPart = part
        }
      }
    }
    nearestPart
  }
}
